import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Transaction } from '@/types';
import { ReceiptTemplate } from './ReceiptTemplate';
import { EmailReceiptDialog } from './EmailReceiptDialog';
import { generateReceiptPDF } from '@/lib/reports/receipt-pdf';
import { useCompany } from '@/hooks/useCompany';
import { Printer, Download, Mail, X } from 'lucide-react';

interface ReceiptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: Transaction;
  client?: {
    name: string;
    email?: string;
    phone?: string;
  };
}

export function ReceiptModal({
  open,
  onOpenChange,
  transaction,
  client
}: ReceiptModalProps) {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const receiptRef = useRef<HTMLDivElement>(null);
  const { company } = useCompany();

  // Generate receipt number
  const receiptNumber = `R-${new Date().getFullYear()}${String(transaction.id).slice(-6).padStart(6, '0')}`;
  const issueDate = new Date();
  const companyName = company?.name || "Sistema Financeiro";
  const companyLogo = company?.logo_url;

  // Handle print
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow && receiptRef.current) {
      const printContent = receiptRef.current.innerHTML;
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Recibo ${receiptNumber}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              @media print { body { margin: 0; } }
              .no-print { display: none; }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  // Handle PDF download
  const handleDownloadPDF = async () => {
    if (!receiptRef.current) return;

    setIsGeneratingPDF(true);
    try {
      await generateReceiptPDF({
        transaction,
        client,
        receiptNumber,
        issueDate,
        companyName,
        companyLogo
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Handle email
  const handleEmail = () => {
    setIsEmailDialogOpen(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Visualizar Recibo</DialogTitle>
            <DialogDescription>
              Recibo da transação de recebimento
            </DialogDescription>
          </DialogHeader>

          {/* Action Buttons */}
          <div className="flex gap-2 mb-4 no-print">
            <Button
              onClick={handlePrint}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Printer className="h-4 w-4" />
              Imprimir
            </Button>
            <Button
              onClick={handleDownloadPDF}
              variant="outline"
              size="sm"
              disabled={isGeneratingPDF}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {isGeneratingPDF ? 'Gerando...' : 'Baixar PDF'}
            </Button>
            {client?.email && (
              <Button
                onClick={handleEmail}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Mail className="h-4 w-4" />
                Enviar por E-mail
              </Button>
            )}
          </div>

          <Separator className="mb-4 no-print" />

          {/* Receipt Preview */}
          <div ref={receiptRef} className="print:shadow-none">
            <ReceiptTemplate
              transaction={transaction}
              client={client}
              receiptNumber={receiptNumber}
              issueDate={issueDate}
              companyName={companyName}
              companyLogo={companyLogo}
            />
          </div>

          {/* Close Button */}
          <div className="flex justify-end mt-6 no-print">
            <Button
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <EmailReceiptDialog
        open={isEmailDialogOpen}
        onOpenChange={setIsEmailDialogOpen}
        transaction={transaction}
        client={client}
        receiptNumber={receiptNumber}
        issueDate={issueDate}
        companyName={companyName}
        companyLogo={companyLogo}
      />
    </>
  );
}